import React, { Fragment } from 'react';

import Related from 'components/Related/Related';
import RichText from 'components/RichText/RichText';
import Text from 'components/Text/Text';
import ThreeRowsLayout from 'components/ThreeRowsLayout/ThreeRowsLayout';
import Toast from 'components/Toast/Toast';
import useClipboard from 'hooks/useClipboard';
import { useCopyStore } from 'store';
import { appState } from 'utils/appState';
import { OfficeCMS } from 'utils/sharedStrapiQueries/sharedTypes';

import * as S from './ContactOfficeCard.styles';

export interface ContactOfficeCardProps {
  office: OfficeCMS;
}

const defaultProps: Partial<ContactOfficeCardProps> = {
  office: {
    id: 0,
    heading: '',
    contactLabel: '',
    telephoneNumber: '',
    addressLabel: '',
    addressLine1: '',
    addressButton: { id: '1', label: '', href: '/', isExternal: false },
    image: { url: '', alternativeText: '' },
    latitude: 0,
    longitude: 0,
    emailFields: [],
    video: { url: '', alternativeText: '' },
    mediaType: 'image',
    telephoneNumber2: '',
    addressButton2: { id: '2', label: '', href: '/', isExternal: false },
    addressLine2: '',
    emailFields2: [],
    textField: '',
    emails: [],
    contactText: '',
    introCopy: '',
    expertiseSectionLabel: '',
    expertiseSectionCategories: [],
    getInTouchSectionLabel: '',
    getInTouchSectionCategories: [],
    getInTouchActiveLabel: false,
    findUsLabel: '',
    contactUsLabel: '',
    relatedArticlesSectionLabel: '',
    relatedArticlesInsights: [],
    relatedArticlesNews: [],
    relatedArticlesCaseStudies: [],
  },
};

const ContactOfficeCard: React.FunctionComponent<ContactOfficeCardProps> = ({
  office,
  ...rest
}) => {
  const { globalSettings } = useCopyStore(({ copy }) => copy);
  const { isCopied, copyToClipboard } = useClipboard();

  const {
    contactLabel,
    telephoneNumber,
    addressLabel,
    addressLine1,
    addressButton,
    emailFields,
    telephoneNumber2,
    addressLine2,
    addressButton2,
    emailFields2,
    contactText,
    textField,
    emails,
    introCopy,
    expertiseSectionLabel,
    expertiseSectionCategories,
    getInTouchSectionLabel,
    getInTouchSectionCategories,
    findUsLabel,
    contactUsLabel,
    relatedArticlesSectionLabel,
    relatedArticlesInsights,
    relatedArticlesNews,
    relatedArticlesCaseStudies,
  } = office;

  const addressLines = [
    {
      id: 1,

      addressLine: addressLine1,
      addressButton: addressButton,
      telephoneNumber: telephoneNumber,
      emailFields: emailFields,
      emails,
    },
    {
      id: 2,

      addressLine: addressLine2,
      addressButton: addressButton2,
      telephoneNumber: telephoneNumber2,
      emailFields: emailFields2,
      emails: null,
    },
  ];

  return (
    <S.Wrapper {...rest}>
      <S.RichTextWrapper richText={textField} />

      {introCopy && (
        <RichText
          as="h2"
          size="heading2"
          richText={introCopy}
          defaultFormatting
        />
      )}

      {expertiseSectionLabel && (
        <S.ExpertiseSection
          label={expertiseSectionLabel}
          categories={expertiseSectionCategories}
        />
      )}

      {getInTouchSectionLabel && (
        <S.GetInTouchSection
          label={getInTouchSectionLabel}
          categories={getInTouchSectionCategories}
          onClickLabel={() => {
            copyToClipboard(emailFields[0]?.email);
            appState.analytics.trackEmailCopy('get in touch');
          }}
        />
      )}

      {addressLines.map(
        ({
          id,
          addressLine,
          addressButton,
          telephoneNumber,
          emailFields,
          emails,
        }) => (
          <Fragment key={id}>
            {(findUsLabel || addressLabel) && (addressLine || addressButton) && (
              <ThreeRowsLayout
                leftSideContent={
                  <RichText
                    as="h6"
                    size="smallBody"
                    richText={findUsLabel ?? addressLabel}
                    defaultFormatting
                  />
                }
                rightSideContent={
                  <>
                    <S.TextRich richText={addressLine} />
                    <Text
                      as="a"
                      size="smallBody"
                      text={addressButton?.label}
                      title={addressButton?.label}
                      href={addressButton?.href}
                      target="_blank"
                      rel="noreferrer"
                    />

                    {addressButton?.socials && (
                      <S.SocialsContainer>
                        <RichText
                          as="p"
                          size="heading4"
                          richText={addressButton?.socials}
                          defaultFormatting
                        />
                      </S.SocialsContainer>
                    )}
                  </>
                }
              />
            )}

            {(contactUsLabel || contactLabel) &&
              (telephoneNumber || emailFields.length > 0) && (
                <ThreeRowsLayout
                  leftSideContent={
                    <RichText
                      as="h6"
                      size="smallBody"
                      richText={contactUsLabel ?? contactLabel}
                      defaultFormatting
                    />
                  }
                  rightSideContent={
                    <>
                      <Text
                        as="a"
                        size="smallBody"
                        href={`tel:${telephoneNumber}`}
                        text={telephoneNumber}
                      />

                      <S.TextRich
                        richText={contactText}
                        margin={
                          !!contactText?.length && !!telephoneNumber?.length
                        }
                      />
                      <S.Emails margin={!!contactText?.length}>
                        {emailFields.map(({ title, email }) => (
                          <Text
                            key={title + email}
                            as="span"
                            size="smallBody"
                            text={title}
                            onClick={() => {
                              copyToClipboard(email);
                              appState.analytics.trackEmailCopy('contact us');
                            }}
                          />
                        ))}
                      </S.Emails>
                    </>
                  }
                />
              )}

            {emails?.map(({ id, title, email }) => (
              <ThreeRowsLayout
                key={id}
                leftSideContent={<Text size="smallBody" text={title} />}
                rightSideContent={
                  <Text
                    as="span"
                    size="smallBody"
                    text={email}
                    onClick={() => {
                      copyToClipboard(email);
                      appState.analytics.trackEmailCopy('contact us');
                    }}
                  />
                }
              />
            ))}
          </Fragment>
        )
      )}

      {relatedArticlesSectionLabel && (
        <Related
          label={relatedArticlesSectionLabel}
          articles={[
            ...(relatedArticlesInsights || []),
            ...(relatedArticlesNews || []),
            ...(relatedArticlesCaseStudies || []),
          ]}
        />
      )}

      {isCopied && <Toast text={globalSettings?.copiedText || '✓'} />}
    </S.Wrapper>
  );
};

ContactOfficeCard.defaultProps = defaultProps;

export default ContactOfficeCard;
